import {
	LoadingOptions, TCargoEOS, TLocation, TRUCK_SIZE, TShipmentAddress, TShipmentEOS
} from '@types';

const PALLET_JACK_HANDLING_KEY = 'pallet_jack_required';

export const hasLocationManagedModifier = (
	stop: Pick<TShipmentAddress, 'location'>,
	loadingOption: LoadingOptions,
) => {
	return stop.location?.loading_options?.[loadingOption] ?? false;
}

type TLocationModifierFlags = {
	/* Gates the tailgate modifier (AP2-1567). */
	locationManagedTailgate: boolean;
	/* Gates hand unload and pallet jack (AP2-1626). */
	autoApplyModifiers: boolean;
}

type TStopLocationModifiers = {
	location?: TShipmentAddress['location'];
	requires_tailgate_lift?: boolean;
	requires_hand_unload?: boolean;
	truck_sizes_requiring_split?: TRUCK_SIZE[];
}

/* Builds the per-stop modifier data to set on a shipment address,
   based on the information from the selected (Location Manager) location.
   requires_tailgate_lift is always derived from the location; the other
   modifiers are only written when their feature flag is enabled. */
export const buildModifiersFromLocation = (
	selectedLocation: Partial<TLocation> | undefined,
	flags: TLocationModifierFlags,
): TStopLocationModifiers => {
	const { locationManagedTailgate, autoApplyModifiers } = flags;

	const location = selectedLocation?.loading_options
		? { loading_options: selectedLocation.loading_options }
		: undefined;
	return {
		...((locationManagedTailgate || autoApplyModifiers) && { location }),
		requires_tailgate_lift: hasLocationManagedModifier({ location }, LoadingOptions.TAIL_GATE),
		...(autoApplyModifiers && {
			requires_hand_unload: hasLocationManagedModifier({ location }, LoadingOptions.HAND_LOAD),
			truck_sizes_requiring_split: selectedLocation?.split_trailer_truck_sizes ?? [],
		}),
	};
}

/* Applies the shipment-level modifiers (pallet jack) from Location Manager.
   Pallet jack is stored on the shipment rather than per-stop, so it is applied
   or cleared whenever the shipment's stops start or stop being managed for a
   pallet jack. */
export const buildShipmentModifiersFromLocation = (
	shipment: TShipmentEOS,
	autoApplyModifiers: boolean,
): TShipmentEOS => {
	if (!autoApplyModifiers) return shipment;

	const needsPalletJack = shipment.cargos.some((c) =>
		hasLocationManagedModifier(c.pickup, LoadingOptions.PALLET_JACK)
			|| hasLocationManagedModifier(c.dropoff, LoadingOptions.PALLET_JACK)
	);
	const palletHandling = needsPalletJack
		? (shipment.pallet_handling.includes(PALLET_JACK_HANDLING_KEY)
			? shipment.pallet_handling
			: [...shipment.pallet_handling, PALLET_JACK_HANDLING_KEY])
		: shipment.pallet_handling.filter((key) => key !== PALLET_JACK_HANDLING_KEY);
	return {
		...shipment,
		need_pallet_jack: needsPalletJack,
		pallet_handling: palletHandling,
	};
}

export const countLocationManagedModifiers = (cargo: TCargoEOS[]) => {
	const managedOptions = [
		LoadingOptions.HAND_LOAD,
		LoadingOptions.TAIL_GATE,
		LoadingOptions.PALLET_JACK,
	];
	// Excluding split trailer, because users can't change it on page 1 of the booking form.
	return managedOptions.filter((option) =>
		cargo.some((c) => hasLocationManagedModifier(c.pickup, option) || hasLocationManagedModifier(c.dropoff, option))
	).length;
}
